import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AlertInfo, AlertService, AlertType } from '../Alert/alert.service';
import { BaseService } from '../base.service';

export interface BulkCancelReservationFailure {
  reservationId: number;
  reason: string;
}

export interface BulkCancelReservationsResult {
  cancelledReservationIds: number[];
  failedReservations: BulkCancelReservationFailure[];
  notificationFailedReservationIds: number[];
}

export interface ApiResponse<T> {
  value: T;
}

export type MyTeamCheckInStatus = 'Checked In' | 'Booked' | 'Missed Check-In' | 'No Booking';

export interface MyTeamWeekRow {
  userId: number;
  name: string;
  statuses: MyTeamCheckInStatus[];
}

export interface MyTeamWeekResponse {
  weekStart: string;
  days: string[];
  team: MyTeamWeekRow[];
}

@Injectable({
  providedIn: 'root',
})
export class ReservationService extends BaseService {
  constructor(
    private http: HttpClient,
    alertService: AlertService
  ) {
    super('reservation', alertService);
  }

  getTimeSlots(): Observable<any> {
    const url = `${this.url}/timePeriods`;
    return this.http.get(url).pipe(
      map((data) => data),
      // No global alert: Home binds time periods to the search form; fail closed with an empty list.
      catchError(() => of({ value: [] }))
    );
  }

  checkForTimeConflict(
    timeSlotId: number,
    body: any,
    delegateUserId: number | null = null
  ): Observable<any> {
    if (timeSlotId == undefined || body['startDate'] == undefined) {
      return new Observable<any>(); // return if params are not set yet
    }

    if ('endDate' in body && 'Recurrence' in body) {
      if (body['endDate'] == undefined || body['Recurrence'] == undefined) {
        return new Observable<any>(); // return if params are not set yet
      }
    }

    this.clearAlert();

    var url = `${this.url}/time-conflict`;

    if (delegateUserId) {
      url += '/' + delegateUserId;
    }

    var params = new HttpParams();
    params = params.set('timeSlotId', timeSlotId.toString());

    return this.http.post(url, body, { params: params }).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error checking for time conflict', err))
    );
  }

  bookReservation(
    workstationId: number,
    floorId: number,
    timeSlotId: number,
    body: any,
    delegateUserId: number | null = null
  ): Observable<any> {
    var url = this.url;

    if (delegateUserId) {
      url += '/' + delegateUserId;
    }

    var params = new HttpParams();

    params = params.set('workstationId', workstationId.toString());
    params = params.set('timeSlotId', timeSlotId.toString());
    params = params.set('floorId', floorId.toString());

    return this.http.post(url, body, { params: params }).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error making booking(s)', err))
    );
  }

  getMyReservations(pageNumber: number, pageSize: number): Observable<any> {
    var params = new HttpParams();
    params = params.set('pageNumber', pageNumber.toString());
    params = params.set('pageSize', pageSize.toString());

    const url = this.url + '/me';
    return this.http.get(url, { params: params }).pipe(
      map((data) => data),
      catchError((err) => this.handleError("Error getting user's bookings", err))
    );
  }

  getAllReservations(
    pageNumber: number,
    pageSize: number,
    reservationFilter: string
  ): Observable<any> {
    var params = new HttpParams();
    params = params.set('pageNumber', pageNumber.toString());
    params = params.set('pageSize', pageSize.toString());
    params = params.set('reservationFilter', reservationFilter);

    const url = this.url + '/all';
    return this.http.get(url, { params: params }).pipe(
      map((data) => data),
      catchError((err) => this.handleError("Error getting all user's bookings", err))
    );
  }

  cancelReservation(id: number): Observable<any> {
    const url = this.url + '/' + id;
    return this.http.delete(url).pipe(
      map((data) => data),
      catchError((err) => this.handleError("Error cancelling user's bookings", err))
    );
  }

  cancelReservations(
    reservationIds: number[]
  ): Observable<ApiResponse<BulkCancelReservationsResult>> {
    const url = `${this.url}/bulk-cancel`;
    return this.http
      .post<ApiResponse<BulkCancelReservationsResult>>(url, { reservationIds })
      .pipe(catchError((err) => this.handleError("Error cancelling user's bookings", err)));
  }

  canceluserAllReservation(): Observable<any> {
    'use strict';
    const url = this.url + '/';
    return this.http.delete(url).pipe(
      map((data) => data),
      catchError((err) => this.handleError("Error cancelling user's bookings", err))
    );
  }

  cancelUserAllReservationsByUserId(userId: number): Observable<any> {
    const url = this.url + '/user/' + userId;
    return this.http.delete(url).pipe(
      map((data) => data),
      catchError((err) => this.handleError("Error cancelling user's bookings", err))
    );
  }

  resend(id: number): Observable<any> {
    const url = this.url + '/resend/' + id;
    return this.http.patch(url, null).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error sending email', err))
    );
  }

  checkIn(id: number): Observable<any> {
    const url = this.url + '/checkin/' + id;
    return this.http.patch(url, null).pipe(
      map((data) => data),
      catchError((err) => {
        const serverMessage: string = this.escapeHtml(err?.error?.message || 'Unknown error.');

        const fullMessage: string = `${serverMessage}<br><br>If you are onsite and check-in still fails, disconnect from Global Protect VPN and try checking in again. See the <a href="/docs/workspace-usage/check-in">Check In user guide</a> for steps.`;

        return this.handleError('Error checking in', { error: { message: fullMessage } });
      })
    );
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  checkOut(id: number): Observable<any> {
    const url = this.url + '/checkout/' + id;
    return this.http.patch(url, null).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error checking out', err))
    );
  }

  getMyTeamLocations(date?: string): Observable<any> {
    let params = new HttpParams();

    if (date) {
      params = params.set('date', date);
    }

    return this.http.get(`${this.url}/my-team`, { params }).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Getting Team Locations', err))
    );
  }

  getMyTeamWeek(date?: string): Observable<ApiResponse<MyTeamWeekResponse>> {
    let params = new HttpParams();

    if (date) {
      params = params.set('date', date);
    }

    return this.http
      .get<ApiResponse<MyTeamWeekResponse>>(`${this.url}/my-team/week`, { params })
      .pipe(catchError((err) => this.handleError('Error Getting Team Week', err)));
  }
}
